import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { accessControlAction } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export interface IntercomRequest {
  id: string;
  device_id: string;
  visitor_name: string;
  status: string;
  created_at: string;
  expires_at: string;
  device?: { name: string; device_code: string };
}

export function useIntercomRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['intercom-requests', user?.id],
    queryFn: async (): Promise<IntercomRequest[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('intercom_requests')
        .select(`
          id,
          device_id,
          visitor_name,
          status,
          created_at,
          expires_at,
          device:ziplink_devices (name, device_code)
        `)
        .eq('notify_user_id', user.id)
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      return (data ?? []).map((row) => ({
        ...row,
        device: Array.isArray(row.device) ? row.device[0] : row.device,
      })) as IntercomRequest[];
    },
    enabled: !!user,
    refetchInterval: 5000, // Poll every 5 seconds
  });
}

export function useRespondIntercom() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ requestId, action }: { requestId: string; action: 'approve' | 'deny' }) => {
      const result = await accessControlAction<{ status: string }>('intercom-respond', {
        request_id: requestId,
        action,
      });
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intercom-requests', user?.id] });
    },
  });
}
